/**
 * PivotChain — visual provenance diagram for breach enrichment.
 *
 * Layout (left → right columns):
 *   [SEED]  →  [R1 Breach DBs]  →  [Pivot Identifiers]  →  [R2 Discoveries]
 *
 * Each node is connected by a CSS-drawn dashed line.
 * Coloring follows validation status: validated=green, unverified=amber, R1=muted.
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight, Database, GitBranch, Search, CheckCircle2, AlertCircle } from 'lucide-react';
import type { IdentifierCluster } from '@/types/profiler';

// ── helpers ────────────────────────────────────────────────────────────────────

function isBreachSource(source: string) {
  return source.startsWith('breach') || source === 'breach_validated';
}

function isPivotSource(source: string) {
  return source.includes('breach_pivot');
}

function isValidatedPivot(source: string) {
  return source === 'breach_validated' || source === 'breach_pivot(validated)';
}

// ── sub-components ─────────────────────────────────────────────────────────────

function NodeCard({
  color,
  icon: Icon,
  label,
  sub,
  chips,
  dim,
}: {
  color: string;
  icon: React.ElementType;
  label: string;
  sub?: string;
  chips?: string[];
  dim?: boolean;
}) {
  const [showAll, setShowAll] = useState(false);
  const visibleChips = showAll ? (chips ?? []) : (chips ?? []).slice(0, 4);
  const overflow = (chips?.length ?? 0) - 4;

  return (
    <div
      style={{
        background: dim ? '#090D13' : '#0C1118',
        border: `1px solid ${color}40`,
        borderLeft: `3px solid ${color}`,
        borderRadius: 8,
        padding: '8px 10px',
        minWidth: 160,
        maxWidth: 220,
        opacity: dim ? 0.6 : 1,
      }}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={10} style={{ color, flexShrink: 0 }} />
        <span style={{ color, fontSize: 9, fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.1em' }}>
          {label}
        </span>
      </div>
      {sub && (
        <span style={{ color: '#5A7A9A', fontSize: 8, fontFamily: 'monospace', display: 'block', marginBottom: 4 }}>
          {sub}
        </span>
      )}
      {chips && chips.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {visibleChips.map((c, i) => (
            <span
              key={i}
              style={{
                background: `${color}10`,
                color: `${color}CC`,
                border: `1px solid ${color}25`,
                fontSize: 7,
                fontFamily: 'monospace',
                padding: '1px 5px',
                borderRadius: 4,
                letterSpacing: '0.04em',
              }}
            >
              {c}
            </span>
          ))}
          {!showAll && overflow > 0 && (
            <button
              onClick={() => setShowAll(true)}
              style={{
                background: '#0F1928',
                color: '#283A50',
                border: '1px solid #182030',
                fontSize: 7,
                fontFamily: 'monospace',
                padding: '1px 5px',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            >
              +{overflow} more
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Arrow({ color = '#182030', dashed = false }: { color?: string; dashed?: boolean }) {
  return (
    <div className="flex items-center flex-shrink-0" style={{ width: 28, position: 'relative' }}>
      <div
        style={{
          width: '100%',
          height: 1,
          background: dashed
            ? `repeating-linear-gradient(to right, ${color} 0 4px, transparent 4px 8px)`
            : color,
        }}
      />
      <div
        style={{
          position: 'absolute',
          right: 0,
          width: 0,
          height: 0,
          borderTop: '3px solid transparent',
          borderBottom: '3px solid transparent',
          borderLeft: `5px solid ${color}`,
        }}
      />
    </div>
  );
}

// ── main ───────────────────────────────────────────────────────────────────────

interface Props {
  cluster: IdentifierCluster;
}

export function PivotChain({ cluster }: Props) {
  const [collapsed, setCollapsed] = useState(false);

  // Separate variants by round
  const r1Variants = cluster.variants.filter(
    v => isBreachSource(v.source) && !isPivotSource(v.source) && v.source !== 'breach_validated'
  );
  const r2Variants = cluster.variants.filter(v => isPivotSource(v.source) || v.source === 'breach_validated');
  const inputVariants = cluster.variants.filter(v => !isBreachSource(v.source));

  // R1: collect all unique breach DBs
  const r1Dbs = Array.from(
    new Set(r1Variants.flatMap(v => v.breach_databases ?? []))
  );

  // Pivot identifiers: unique values from R1 that were used to pivot (have r2 variants pointing at them)
  const pivotIdentifiers = Array.from(
    new Set(r2Variants.map(v => v.pivot_via).filter(Boolean) as string[])
  );

  const hasBreach = r1Variants.length > 0 || r2Variants.length > 0;

  if (!hasBreach) {
    return (
      <div
        style={{
          background: '#0C1118',
          border: '1px solid #182030',
          borderRadius: 8,
          padding: '20px',
          textAlign: 'center',
        }}
      >
        <span style={{ color: '#283A50', fontSize: 10, fontFamily: 'monospace' }}>
          No breach enrichment performed on this entity
        </span>
      </div>
    );
  }

  return (
    <div style={{ background: '#090D13', border: '1px solid #182030', borderRadius: 8, overflow: 'hidden' }}>
      {/* header */}
      <button
        onClick={() => setCollapsed(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors"
        style={{ borderBottom: collapsed ? undefined : '1px solid #182030' }}
      >
        <div className="flex items-center gap-2">
          <GitBranch size={12} style={{ color: '#00C8FF' }} />
          <span style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em' }}>
            BREACH PIVOT CHAIN
          </span>
          <span
            style={{
              background: '#00C8FF15',
              color: '#00C8FF',
              border: '1px solid #00C8FF30',
              fontSize: 7,
              fontFamily: 'monospace',
              padding: '1px 6px',
              borderRadius: 99,
            }}
          >
            {r1Dbs.length} DBs · {pivotIdentifiers.length} pivots · {r2Variants.length} R2 hits
          </span>
        </div>
        {collapsed
          ? <ChevronRight size={12} style={{ color: '#5A7A9A' }} />
          : <ChevronDown size={12} style={{ color: '#5A7A9A' }} />}
      </button>

      {!collapsed && (
        <div className="p-4 overflow-x-auto">
          {/* ── Flow: Seed → R1 DBs → Pivots → R2 ── */}
          <div className="flex items-start gap-0 flex-nowrap min-w-max">

            {/* Column 1: Seed */}
            <div className="flex flex-col gap-2">
              <span style={{ color: '#283A50', fontSize: 7, fontFamily: 'monospace', letterSpacing: '0.12em', marginBottom: 4 }}>
                SEED
              </span>
              <NodeCard
                color="#00C8FF"
                icon={Search}
                label={cluster.canonical}
                sub={inputVariants.length > 0 ? `${inputVariants.length} input identifiers` : undefined}
              />
            </div>

            <Arrow color="#00C8FF80" />

            {/* Column 2: R1 Breach DBs */}
            <div className="flex flex-col gap-2">
              <span style={{ color: '#283A50', fontSize: 7, fontFamily: 'monospace', letterSpacing: '0.12em', marginBottom: 4 }}>
                ROUND 1 · BREACH DBS
              </span>
              <NodeCard
                color="#5A7A9A"
                icon={Database}
                label={`${r1Dbs.length} breach databases`}
                sub={`${r1Variants.length} identifiers extracted`}
                chips={r1Dbs}
              />
            </div>

            {pivotIdentifiers.length > 0 && (
              <>
                <Arrow color="#5A7A9A80" dashed />

                {/* Column 3: Pivot identifiers */}
                <div className="flex flex-col gap-2">
                  <span style={{ color: '#283A50', fontSize: 7, fontFamily: 'monospace', letterSpacing: '0.12em', marginBottom: 4 }}>
                    ROUND 2 · PIVOT QUERIES
                  </span>
                  <div className="flex flex-col gap-1.5">
                    {pivotIdentifiers.map((pid, i) => {
                      const r2ForPivot = r2Variants.filter(v => v.pivot_via === pid);
                      const validated = r2ForPivot.some(v => isValidatedPivot(v.source));
                      const r2Dbs = Array.from(
                        new Set(r2ForPivot.flatMap(v => v.breach_databases ?? []))
                      );
                      return (
                        <NodeCard
                          key={i}
                          color={validated ? '#00C853' : '#F0B429'}
                          icon={validated ? CheckCircle2 : AlertCircle}
                          label={pid}
                          sub={validated ? 'bidirectionally validated' : 'not bidirectionally validated'}
                          chips={r2Dbs.slice(0, 6)}
                        />
                      );
                    })}
                  </div>
                </div>

                <Arrow color="#283A5080" dashed />

                {/* Column 4: R2 Discoveries */}
                <div className="flex flex-col gap-2">
                  <span style={{ color: '#283A50', fontSize: 7, fontFamily: 'monospace', letterSpacing: '0.12em', marginBottom: 4 }}>
                    R2 DISCOVERIES
                  </span>
                  <div className="flex flex-col gap-1.5">
                    {pivotIdentifiers.map((pid, i) => {
                      const r2ForPivot = r2Variants.filter(v => v.pivot_via === pid);
                      const validated = r2ForPivot.some(v => isValidatedPivot(v.source));
                      const byType: Record<string, string[]> = {};
                      for (const v of r2ForPivot) {
                        (byType[v.type] = byType[v.type] || []).push(v.value);
                      }
                      const summary = Object.entries(byType)
                        .map(([t, vals]) => `${vals.length} ${t}`)
                        .join(' · ');
                      return (
                        <NodeCard
                          key={i}
                          color={validated ? '#00C853' : '#F0B429'}
                          icon={Database}
                          label={`${r2ForPivot.length} new identifiers`}
                          sub={summary || undefined}
                          chips={r2ForPivot.slice(0, 8).map(v => v.value)}
                          dim={!validated}
                        />
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-4 pt-3" style={{ borderTop: '1px solid #0F1928' }}>
            <span style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace' }}>LEGEND:</span>
            {[
              { color: '#00C853', label: 'Bidirectionally validated — high confidence' },
              { color: '#F0B429', label: 'Pivot not validated — treat with caution' },
              { color: '#5A7A9A', label: 'Round 1 breach extraction' },
            ].map(({ color, label }) => (
              <div key={label} className="flex items-center gap-1.5">
                <div style={{ width: 6, height: 6, borderRadius: 99, background: color, flexShrink: 0 }} />
                <span style={{ color: '#283A50', fontSize: 7, fontFamily: 'monospace' }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
